import { Group } from 'three';

import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { KitchenBlueprintInput } from '../types';
import type { SceneComposition, SceneObject } from '../../../types/engine.types';
import {
	getFloorMaterialCfn,
	loadCriticalTexturesCfn,
	loadSceneMaterialsCfn,
	queueBackgroundPreloadsCfn,
	loadApplianceTexturesCfn
} from './load-textures.cfn';
import { analyzeTextureRequirementsCfn, type SceneTextureRequirements } from './analyze-texture-requirements.cfn';
import { buildSceneManifestCfn } from './build-scene-manifest.cfn';
import { createIslandGroupCfn, findDefaultIslandIndexCfn, getIslandRenderOrderCfn } from './create-island.cfn';
import { createSurroundPanelObjectCfn, createTrimPanelObjectCfn, createCountertopObjectCfn } from './create-geometry.cfn';
import { generateCutoutsCfn } from './generate-cutouts.cfn';
import { loadAppliancesProgressiveTypedCfn } from './load-appliances.cfn';
import { calculateKitchenBoundsCfn, calculateCameraConfigCfn } from './camera-bounds.cfn';
import { normalizeSurroundKey, normalizeCountertopKey } from '../helpers';

/**
 * Build scene progressively - yields objects as they're created.
 * This allows for non-blocking scene display where objects appear incrementally.
 *
 * Yield order (per island, sequentially):
 * 1. Floor (visible immediately)
 * 2. For each island in sequence:
 *    a. Island group container
 *    b. Panels (type: 'panel')
 *    c. Corners/trims (type: 'corner')
 *    d. Countertop (type: 'countertop')
 * 3. Cutouts (type: 'cutout')
 * 4. Appliances (type: 'appliance')
 *
 * The final return value contains bounds and camera configuration.
 */
export async function* buildProgressiveCfn(
	self: KitchenSceneBuilder,
	{ input }: { input: KitchenBlueprintInput }
): AsyncGenerator<SceneObject, SceneComposition, void> {
	// Reset the sky ready flag for this build (prevents duplicate onSkyReady calls)
	(self as any)._skyReadyCalled = false;

	// Step 1: Initialize kitchen data
	if (input.kitchen) {
		self.kitchenDataManagerAccess.init(input.kitchen);
	} else {
		console.warn('KitchenSceneBuilder.buildProgressive: No kitchen data provided!');
	}

	self.currentManifestAccess = buildSceneManifestCfn(self, { input });

	// Step 2: Calculate and yield camera/bounds FIRST (before any objects)
	// This allows the engine to position the camera before rendering starts
	const kitchenSize = input.kitchen?.size ?? { width: 96, depth: 36, height: 50 };
	const bounds = calculateKitchenBoundsCfn(self, { kitchenSize });
	const camera = calculateCameraConfigCfn(self, { kitchenSize, zoom: input.controller?.viewport?.state?.zoom ?? 1.0 });

	// Step 2b: Analyze texture requirements for phased loading
	const requirements = analyzeTextureRequirementsCfn(self, { input });

	// Step 3: Phase 1 - Load ONLY floor + sky (critical textures)
	// This allows sky background and floor to appear immediately
	await loadCriticalTexturesCfn(self, {
		floorKey: requirements.critical.floor,
		skyKey: requirements.critical.sky
		// Note: sceneMaterials are loaded AFTER floor is visible
	});

	// Yield setup object with camera configuration - engine should apply this immediately
	// The engine will call ensureEnvironmentMapForReflections() to set sky as background
	yield {
		object: new Group(), // Empty placeholder object (won't be added to scene)
		metadata: {
			id: 'scene-setup',
			type: 'setup',
			source: self.name,
			camera,
			bounds,
			skyKey: requirements.critical.sky,
			floorKey: requirements.critical.floor
		}
	};

	// Step 4: Create and yield floor first (so something is visible immediately)
	const floorMaterial = getFloorMaterialCfn(self, { floorKey: input.floorKey });
	const floor = self.geometryBuilderAccess.createFloor(floorMaterial);
	yield {
		object: floor,
		metadata: {
			id: 'floor',
			type: 'floor',
			source: self.name
		}
	};

	// Step 4b: Now load scene materials (surround, countertop, trim) in background
	// These load in parallel while the floor is visible
	// Kitchen geometry will wait for these via the texture queue's waitForTexture()
	const sceneMaterialsPromise = loadSceneMaterialsCfn(self, {
		sceneMaterials: requirements.sceneMaterials
	});

	// Step 4c: Load appliance textures (needed for model material replacement)
	// Pass model requirements so textures are skipped entirely when no appliances are placed.
	// This runs in parallel with scene texture loading
	const applianceTexturesPromise = loadApplianceTexturesCfn(self, {
		modelRequirements: requirements.models
	});

	// Step 4: Create island containers and their geometry progressively
	const islands = self.kitchenDataManagerAccess.getIslands();
	self.clearIslandGroups();

	// Find default island (marked with isDefault: true) and compute BFS render order
	const defaultIndex = findDefaultIslandIndexCfn(self, { islands });
	const renderOrder = getIslandRenderOrderCfn(self, { islands, defaultIndex });

	// Store render order for appliance sorting
	self.setIslandRenderOrder(renderOrder);

	// Pre-create all island groups so they can be referenced during progressive build
	for (let i = 0; i < islands.length; i++) {
		const island = islands[i];
		const islandGroup = createIslandGroupCfn(self, { island, index: i });
		self.addIslandGroup(islandGroup);
	}

	// Group indices by wave for parallel rendering
	const waveMap = new Map<number, number[]>();
	for (const { index, wave } of renderOrder) {
		if (!waveMap.has(wave)) {
			waveMap.set(wave, []);
		}
		waveMap.get(wave)!.push(index);
	}

	// Render each wave (wave 0 = default alone, then parallel left/right)
	const sortedWaves = Array.from(waveMap.keys()).sort((a, b) => a - b);
	for (const wave of sortedWaves) {
		const indicesInWave = waveMap.get(wave)!;

		// Within a wave, yield islands sequentially (engine handles parallel animation)
		for (const i of indicesInWave) {
			const island = islands[i];
			const islandGroup = self.islandGroupsAccess[i];
			const islandId = island.id ?? `island-${i}`;
			const islandCenter = islandGroup.userData?.center;

			// Yield island container
			yield {
				object: islandGroup,
				metadata: {
					id: islandId,
					type: 'island',
					source: self.name,
					islandIndex: i,
					wave  // Include wave for animation sequencing
				}
			};

			// Yield island geometry progressively (panels, trims, countertop)
			// Geometry creation waits for its required textures via the queue
			yield* createKitchenGeometryProgressiveCfn(self, {
				island,
				islandIndex: i,
				islandGroup,
				islandId,
				islandCenter,
				requirements
			});
		}
	}

	// Step 4b: Yield a flush marker to animate any remaining islands before cutouts/appliances
	// This ensures the final wave gets animated even if React re-renders during the build
	yield {
		object: new Group(), // Empty group, won't be added to scene
		metadata: {
			id: 'flush-islands',
			type: 'flush-islands',
			source: self.name
		}
	};

	// Step 5: Generate and yield cutouts
	if (input.controller?.options?.showCutouts !== false) {
		const debugGroup = await generateCutoutsCfn(self, {});
		if (debugGroup) {
			yield {
				object: debugGroup,
				metadata: {
					id: 'cutout-debug-group',
					type: 'cutout',
					source: self.name
				}
			};
		}
	}

	// Step 7: Ensure scene materials and appliance textures are loaded before loading appliance models
	// Note: Scene materials should already be loaded (geometry waits via waitForTexture),
	// but we await here to catch any errors
	await Promise.all([sceneMaterialsPromise, applianceTexturesPromise]);

	// Step 8: Load appliances progressively
	if (input.controller?.options?.showAppliances !== false) {
		yield* loadAppliancesProgressiveTypedCfn(self, { input });
	}

	// Step 9: Trigger sky quality upgrade now that all models are loaded
	// This downloads the higher-res sky HDR and applies it with a fade transition.
	// Deferred to here so the upgrade download doesn't compete with scene materials/models.
	if (requirements.qualityUpgrades.length > 0) {
		(self as any)._pendingSkyUpgrades = requirements.qualityUpgrades;
		yield {
			object: new Group(),
			metadata: {
				id: 'sky-quality-upgrade',
				type: 'sky-quality-upgrade',
				source: self.name,
				upgrades: requirements.qualityUpgrades,
				skyKey: requirements.critical.sky
			}
		};
	}

	// Step 10: Queue background preloads (non-blocking)
	// These preload other textures for instant switching later
	queueBackgroundPreloadsCfn(self, { input, requirements });

	// Return final composition (camera/bounds already applied via setup object)
	return {
		objects: [], // Objects were already yielded
		bounds,
		camera
	};
}

/**
 * Create kitchen geometry progressively for a single island.
 * Yields panels, trims, and countertop individually with type metadata.
 * Waits for required textures before creating each geometry type (gated rendering).
 */
async function* createKitchenGeometryProgressiveCfn(
	self: KitchenSceneBuilder,
	{ island, islandIndex, islandGroup, islandId, islandCenter, requirements }: {
		island: any;
		islandIndex: number;
		islandGroup: Group;
		islandId: string;
		islandCenter?: { x: number; y: number; z: number };
		requirements: SceneTextureRequirements;
	}
): AsyncGenerator<SceneObject, void, void> {
	const surroundKey = normalizeSurroundKey(self.kitchenDataManagerAccess.getKitchenMaterial()?.surround?.key);
	const textureQueue = self.textureLoadingQueueAccess;

	// Wait for surround texture before creating panels (gated rendering)
	try {
		await textureQueue.waitForTexture(requirements.sceneMaterials.surround);
	} catch {
		console.warn(`buildProgressiveCfn: Surround texture ${requirements.sceneMaterials.surround} failed to load, continuing with fallback`);
	}

	// Yield panels (surround)
	// Panels are added to island group here because their positions are relative to the island
	if (island.panels) {
		for (let panelIndex = 0; panelIndex < island.panels.length; panelIndex++) {
			const panel = island.panels[panelIndex];
			const result = await createSurroundPanelObjectCfn(self, { panel, islandIndex, panelIndex, surroundKey });
			if (result) {
				result.mesh.userData.islandId = islandId;
				result.mesh.userData.islandCenter = islandCenter;
				islandGroup.add(result.mesh);
				yield {
					object: result.mesh,
					metadata: {
						id: result.id,
						type: 'panel',
						source: self.name,
						islandId,
						islandIndex,
						islandCenter
					}
				};
			}
		}
	}

	// Wait for trim texture before creating trims (gated rendering)
	try {
		await textureQueue.waitForTexture(requirements.sceneMaterials.trim);
	} catch {
		console.warn(`buildProgressiveCfn: Trim texture ${requirements.sceneMaterials.trim} failed to load, continuing with fallback`);
	}

	// Yield panel trims (corners)
	if (island.panelTrims) {
		for (let trimIndex = 0; trimIndex < island.panelTrims.length; trimIndex++) {
			const trim = island.panelTrims[trimIndex];
			const result = await createTrimPanelObjectCfn(self, { trim, islandIndex, trimIndex, surroundKey });
			if (result) {
				result.mesh.userData.islandId = islandId;
				result.mesh.userData.islandCenter = islandCenter;
				islandGroup.add(result.mesh);
				yield {
					object: result.mesh,
					metadata: {
						id: result.id,
						type: 'corner',
						source: self.name,
						islandId,
						islandIndex,
						islandCenter
					}
				};
			}
		}
	}

	// Yield countertop -- resolve the key from the per-island countertop material so a
	// per-island "No Countertop"/override is honored (not the kitchen default).
	if (island.counter) {
		const countertopKey = normalizeCountertopKey(island.counter?.material?.key);

		// Wait for countertop texture before creating countertop (gated rendering)
		try {
			await textureQueue.waitForTexture(countertopKey);
		} catch {
			console.warn(`buildProgressiveCfn: Countertop texture ${countertopKey} failed to load, continuing with fallback`);
		}

		const result = await createCountertopObjectCfn(self, { island, islandIndex, countertopKey });
		if (result) {
			result.mesh.userData.islandId = islandId;
			result.mesh.userData.islandCenter = islandCenter;
			islandGroup.add(result.mesh);
			yield {
				object: result.mesh,
				metadata: {
					id: result.id,
					type: 'countertop',
					source: self.name,
					islandId,
					islandIndex,
					islandCenter
				}
			};
		}
	}
}
